import React, { useState, useEffect } from 'react';
import api, { API_URL } from '../services/api';
import toast from '../utils/toast';

const getFullFileUrl = (relativePath) => {
  if (!relativePath) return '';
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath;
  }
  const cleanPath = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
  const baseUrl = API_URL.replace('/api', '');
  return `${baseUrl}/${cleanPath}`;
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const CustomPageExpensesTab = ({ submissionId, customPage, canUpdate, formatDate }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState('');
  const [cost, setCost] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [editCost, setEditCost] = useState('');

  const settings = customPage?.expensesSettings || {};
  const totalCost = expenses.reduce((sum, e) => sum + (Number(e.cost) || 0), 0);

  useEffect(() => {
    if (submissionId && customPage?.allowExpensesTab) {
      fetchExpenses();
    }
  }, [submissionId, customPage?.allowExpensesTab]);

  const fetchExpenses = async () => {
    if (!submissionId) return;
    try {
      setLoading(true);
      const response = await api.get(`/custom-pages/submissions/${submissionId}/expenses`);
      if (response.data.success) {
        setExpenses(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (cost === '' || Number.isNaN(Number(cost)) || Number(cost) < 0) {
      toast.error('Enter a valid cost');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post(`/custom-pages/submissions/${submissionId}/expenses`, {
        description: description.trim(),
        cost: Number(cost),
      });
      if (response.data.success) {
        toast.success('Expense added');
        setDescription('');
        setCost('');
        await fetchExpenses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (expense) => {
    setEditingId(expense._id);
    setEditDescription(expense.description);
    setEditCost(String(expense.cost));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDescription('');
    setEditCost('');
  };

  const handleUpdateExpense = async (expenseId) => {
    if (!editDescription.trim()) {
      toast.error('Description is required');
      return;
    }
    if (editCost === '' || Number.isNaN(Number(editCost)) || Number(editCost) < 0) {
      toast.error('Enter a valid cost');
      return;
    }

    try {
      const response = await api.put(`/custom-pages/submissions/${submissionId}/expenses/${expenseId}`, {
        description: editDescription.trim(),
        cost: Number(editCost),
      });
      if (response.data.success) {
        toast.success('Expense updated');
        cancelEdit();
        await fetchExpenses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense and all its attachments?')) return;
    try {
      const response = await api.delete(`/custom-pages/submissions/${submissionId}/expenses/${expenseId}`);
      if (response.data.success) {
        toast.success('Expense deleted');
        await fetchExpenses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete expense');
    }
  };

  const handleUploadAttachment = async (expenseId, file) => {
    if (!file) return;
    setUploadingFor(expenseId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(
        `/custom-pages/submissions/${submissionId}/expenses/${expenseId}/attachments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data.success) {
        toast.success('File uploaded');
        await fetchExpenses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploadingFor(null);
    }
  };

  const handleDeleteAttachment = async (expenseId, attachmentId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      const response = await api.delete(
        `/custom-pages/submissions/${submissionId}/expenses/${expenseId}/attachments/${attachmentId}`
      );
      if (response.data.success) {
        toast.success('File deleted');
        await fetchExpenses();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete file');
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500 py-4">Loading expenses...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Expenses</h3>
        <p className="text-sm font-semibold text-gray-700">
          Total: {formatCurrency(totalCost)}
        </p>
      </div>

      {expenses.length === 0 ? (
        <p className="text-sm text-gray-500">No expenses added yet.</p>
      ) : (
        <div className="space-y-4">
          {expenses.map(expense => (
            <div key={expense._id} className="border border-gray-200 rounded-lg p-4 bg-white">
              {editingId === expense._id ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <input
                    type="text"
                    value={editDescription}
                    onChange={e => setEditDescription(e.target.value)}
                    className="md:col-span-2 px-3 py-2 text-sm border border-gray-300 rounded-md"
                    placeholder="Description"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editCost}
                    onChange={e => setEditCost(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                    placeholder="Cost"
                  />
                  <div className="md:col-span-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleUpdateExpense(expense._id)}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                  <div>
                    <p className="font-medium text-gray-900">{expense.description}</p>
                    <p className="text-sm text-gray-600">Cost: {formatCurrency(expense.cost)}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Added by {expense.createdBy?.firstName} {expense.createdBy?.lastName}
                      {expense.createdAt && formatDate ? ` · ${formatDate(expense.createdAt)}` : ''}
                    </p>
                  </div>
                  {canUpdate && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(expense)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteExpense(expense._id)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-medium text-gray-500 uppercase mb-2">Attachments</p>
                {expense.attachments?.length > 0 ? (
                  <ul className="space-y-1 mb-3">
                    {expense.attachments.map(att => (
                      <li key={att._id} className="flex items-center justify-between text-sm">
                        <a
                          href={getFullFileUrl(att.fileUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 truncate mr-2"
                        >
                          {att.originalName}
                        </a>
                        {canUpdate && (
                          <button
                            type="button"
                            onClick={() => handleDeleteAttachment(expense._id, att._id)}
                            className="text-red-600 hover:text-red-800 text-xs shrink-0"
                          >
                            Remove
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-400 mb-3">No files uploaded</p>
                )}

                {canUpdate && (
                  <div>
                    <input
                      type="file"
                      accept={settings.allowedFileTypes?.join(',') || '*'}
                      disabled={uploadingFor === expense._id}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadAttachment(expense._id, file);
                        e.target.value = '';
                      }}
                      className="text-sm"
                    />
                    {uploadingFor === expense._id && (
                      <span className="text-xs text-gray-500 ml-2">Uploading…</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {canUpdate && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Add Expense</h4>
          <form onSubmit={handleAddExpense} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Filing fee, courier charges"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={cost}
                  onChange={e => setCost(e.target.value)}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              After saving, upload receipts or supporting files under each expense row.
              Max file size: {settings.maxFileSize || 10}MB
              {settings.allowedFileTypes?.length
                ? ` | Allowed: ${settings.allowedFileTypes.join(', ')}`
                : ''}
            </p>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add Expense'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default CustomPageExpensesTab;
